import { supabase } from './supabase'
import type { HubLang } from './i18n'

/**
 * 147 (CMP-6). Photo contests, for everybody: the monthly popular vote that the
 * contest box enters automatically, themed contests people enter on purpose,
 * voting, judges, and the results.
 *
 * ── WHAT THE PAGE IS NOT TOLD, AND WHY ──────────────────────────────────────
 * While entries and votes are open the server does not send who posted a
 * photo, its post, or any count (photo_contest_gallery) — not "sends and the
 * page hides". The only numbers anybody gets before the results are the
 * entries and the TOTAL votes (photo_contest_list), aggregate by design
 * (rule 8). Judges get who posted each photo; they get counts only once
 * voting has closed, to confirm. The photo's storage path still names its
 * uploader's id, and the photo is on a feed post with a name on it: the
 * contest page says so ("not shown", never "secret").
 *
 * ── THE PHASE IS THE SERVER'S ───────────────────────────────────────────────
 * app.photo_contest_phase() reads the database's clock in the contest's own
 * zone. The page never works out a phase from the dates — a reader's clock
 * would disagree with the server's exactly at the boundary (093's lesson).
 */

export type ContestPhase = 'upcoming' | 'entering' | 'voting' | 'confirming' | 'final' | 'cancelled'
export type ContestKind = 'judged' | 'vote'
export type ContestLocale = 'en-CA' | 'es-CL' | 'fr-FR'
export type ContestWords = { title: string; objective: string | null; prize: string | null }
export type ContestTexts = Partial<Record<ContestLocale, ContestWords>>
export type ContestPlace = { place: number; award: 'place' | 'judges'; stars: number }
export type ContestPerson = { personId: string; name: string }
export type ContestWinner = {
  place: number
  award: 'place' | 'judges'
  /** 1st, 2nd… within its award: a judges' award is "judges' award 1". */
  rank: number
  stars: number
  path: string
  contentId: string | null
  personId: string
  name: string
  votes: number | null
}

export type Contest = {
  id: string
  tenantId: string
  kind: ContestKind
  seriesId: string | null
  periodMonth: string | null
  phase: ContestPhase
  title: string
  objective: string | null
  prize: string | null
  texts: ContestTexts
  startsOn: string
  endsOn: string
  voteEndsOn: string | null
  timezone: string
  photosSince: string | null
  votesPerPerson: number
  maxEntries: number | null
  places: ContestPlace[]
  judges: ContestPerson[]
  entryCount: number
  voteTotal: number
  myVotes: number
  myEntries: number
  isJudge: boolean
  isOrganiser: boolean
  winners: ContestWinner[]
  thumbs: string[]
  onHub: boolean
  seriesVoteDays: number | null
  seriesRunning: boolean
  createdByName: string
  judgedAt: string | null
  judgedByName: string | null
  cancelledAt: string | null
  /** 151. The winners go to the feed when a judge confirms (always for the monthly contest). */
  announceInFeed: boolean
  /** 151. The feed post that did, once it has. */
  announcementId: string | null
}

type Row = Record<string, unknown>

function toContest(r: Row): Contest {
  return {
    id: r.contest_id as string,
    tenantId: r.tenant_id as string,
    kind: r.kind as ContestKind,
    seriesId: (r.series_id as string | null) ?? null,
    periodMonth: (r.period_month as string | null) ?? null,
    phase: r.phase as ContestPhase,
    title: (r.title as string | null) ?? '',
    objective: (r.objective as string | null) ?? null,
    prize: (r.prize as string | null) ?? null,
    texts: (r.texts as ContestTexts | null) ?? {},
    startsOn: r.starts_on as string,
    endsOn: r.ends_on as string,
    voteEndsOn: (r.vote_ends_on as string | null) ?? null,
    timezone: r.timezone as string,
    photosSince: (r.photos_since as string | null) ?? null,
    votesPerPerson: Number(r.votes_per_person ?? 1),
    maxEntries: r.max_entries == null ? null : Number(r.max_entries),
    places: ((r.places as ContestPlace[] | null) ?? []).map((p) => ({ place: p.place, award: p.award, stars: Number(p.stars) })),
    judges: ((r.judges as { person_id: string; name: string }[] | null) ?? []).map((j) => ({ personId: j.person_id, name: j.name })),
    entryCount: Number(r.entry_count ?? 0),
    voteTotal: Number(r.vote_total ?? 0),
    myVotes: Number(r.my_votes ?? 0),
    myEntries: Number(r.my_entries ?? 0),
    isJudge: r.is_judge === true,
    isOrganiser: r.is_organiser === true,
    winners: ((r.winners as Row[] | null) ?? []).map((w) => ({
      place: Number(w.place),
      award: w.award as 'place' | 'judges',
      rank: Number(w.rank),
      stars: Number(w.stars),
      path: w.storage_path as string,
      contentId: (w.content_id as string | null) ?? null,
      personId: w.person_id as string,
      name: w.name as string,
      votes: w.votes == null ? null : Number(w.votes),
    })),
    thumbs: (r.thumbs as string[] | null) ?? [],
    onHub: r.on_hub === true,
    seriesVoteDays: r.series_vote_days == null ? null : Number(r.series_vote_days),
    seriesRunning: r.series_running === true,
    createdByName: r.created_by_name as string,
    judgedAt: (r.judged_at as string | null) ?? null,
    judgedByName: (r.judged_by_name as string | null) ?? null,
    cancelledAt: (r.cancelled_at as string | null) ?? null,
    announceInFeed: r.series_id != null,
    announcementId: null,
  }
}

/** Every contest the caller can see, in hub order (no limit: a dozen a year). */
export async function loadContestList(): Promise<Contest[]> {
  const [list, feed] = await Promise.all([
    supabase.rpc('photo_contest_list'),
    // 151. A separate read rather than two more columns on photo_contest_list
    // (a changed return type is a drop, and migrations do not drop). A failure
    // here costs the feed flags, never the page.
    loadContestFeed().catch(() => new Map<string, { announce: boolean; contentId: string | null }>()),
  ])
  if (list.error) throw list.error
  return ((list.data ?? []) as Row[]).map((r) => {
    const c = toContest(r)
    const f = feed.get(c.id)
    return f ? { ...c, announceInFeed: f.announce, announcementId: f.contentId } : c
  })
}

/** 151. Whether each contest announces in the feed, and the post that did. */
export async function loadContestFeed(): Promise<Map<string, { announce: boolean; contentId: string | null }>> {
  const { data, error } = await supabase.rpc('photo_contest_feed')
  if (error) throw error
  const out = new Map<string, { announce: boolean; contentId: string | null }>()
  for (const r of (data ?? []) as Row[]) {
    out.set(r.contest_id as string, { announce: r.announce_in_feed === true, contentId: (r.content_id as string | null) ?? null })
  }
  return out
}

/** 151. Announce a themed contest's winners in the feed, or not (organisers). */
export async function setAnnounce(contestId: string, on: boolean): Promise<void> {
  const { error } = await supabase.rpc('set_photo_contest_announce', { p_contest_id: contestId, p_on: on })
  if (error) throw error
}

/**
 * 151. The contest a feed post announced, by the post's id — one read shared by
 * every card on the page (a minute's cache), so thirty cards make one call.
 */
let announced: { at: number; p: Promise<Map<string, Contest>> } | null = null
export function contestForPost(contentId: string): Promise<Contest | null> {
  if (!announced || Date.now() - announced.at > 60_000) {
    announced = {
      at: Date.now(),
      p: loadContestList()
        .then((list) => new Map(list.filter((c) => c.announcementId).map((c) => [c.announcementId as string, c])))
        .catch(() => new Map<string, Contest>()),
    }
  }
  return announced.p.then((m) => m.get(contentId) ?? null)
}

/** THE hub card: the first contest the server marks on_hub (it orders them). */
export function hubContest(list: Contest[]): Contest | null {
  return list.find((c) => c.onHub) ?? null
}

export type GalleryEntry = {
  entryId: string
  path: string
  width: number | null
  height: number | null
  descriptionEn: string | null
  descriptionEs: string | null
  isMine: boolean
  myVote: boolean
  entrantId: string | null
  entrantName: string | null
  contentId: string | null
  votes: number | null
  disqualified: boolean
  disqualifyReason: DisqualifyReason | null
  disqualifyNote: string | null
  place: number | null
  award: 'place' | 'judges' | null
}

export const GALLERY_PAGE = 24

/** A contest's photos as the caller may see them, paged by the server (rule 1). */
export async function loadGallery(contestId: string, offset = 0, limit = GALLERY_PAGE): Promise<{ entries: GalleryEntry[]; total: number }> {
  const { data, error } = await supabase.rpc('photo_contest_gallery', {
    p_contest_id: contestId,
    p_limit: limit,
    p_offset: offset,
  })
  if (error) throw error
  const rows = (data ?? []) as Row[]
  return {
    entries: rows.map((r) => ({
      entryId: r.entry_id as string,
      path: r.storage_path as string,
      width: r.width == null ? null : Number(r.width),
      height: r.height == null ? null : Number(r.height),
      descriptionEn: (r.description_en as string | null) ?? null,
      descriptionEs: (r.description_es as string | null) ?? null,
      isMine: r.is_mine === true,
      myVote: r.my_vote === true,
      entrantId: (r.entrant_id as string | null) ?? null,
      entrantName: (r.entrant_name as string | null) ?? null,
      contentId: (r.content_id as string | null) ?? null,
      votes: r.votes == null ? null : Number(r.votes),
      disqualified: r.disqualified === true,
      disqualifyReason: (r.disqualify_reason as DisqualifyReason | null) ?? null,
      disqualifyNote: (r.disqualify_note as string | null) ?? null,
      place: r.place == null ? null : Number(r.place),
      award: (r.award as 'place' | 'judges' | null) ?? null,
    })),
    total: rows.length ? Number(rows[0].total_count) : 0,
  }
}

/** The description to use as a photo's alt text: never a name (the model is told so). */
export function altFor(e: Pick<GalleryEntry, 'descriptionEn' | 'descriptionEs'>, lang: HubLang): string {
  return (lang === 'es' ? e.descriptionEs ?? e.descriptionEn : e.descriptionEn ?? e.descriptionEs) ?? ''
}

export type MyContestPhoto = {
  path: string
  contentId: string
  width: number | null
  height: number | null
  caption: string | null
  postedAt: string
  entered: boolean
}

/** The caller's own photos that could go into a themed contest, paged (rule 1). */
export async function loadMyContestPhotos(contestId: string, offset = 0, limit = GALLERY_PAGE): Promise<{ photos: MyContestPhoto[]; total: number }> {
  const { data, error } = await supabase.rpc('my_contest_photos', {
    p_contest_id: contestId,
    p_limit: limit,
    p_offset: offset,
  })
  if (error) throw error
  const rows = (data ?? []) as Row[]
  return {
    photos: rows.map((r) => ({
      path: r.storage_path as string,
      contentId: r.content_id as string,
      width: r.width == null ? null : Number(r.width),
      height: r.height == null ? null : Number(r.height),
      caption: (r.caption as string | null) ?? null,
      postedAt: r.posted_at as string,
      entered: r.entered === true,
    })),
    total: rows.length ? Number(rows[0].total_count) : 0,
  }
}

export async function enterContest(contestId: string, path: string): Promise<void> {
  const { error } = await supabase.rpc('enter_photo_contest', { p_contest_id: contestId, p_storage_path: path })
  if (error) throw error
}

export async function withdrawEntry(contestId: string, path: string): Promise<void> {
  const { error } = await supabase.rpc('withdraw_photo_entry', { p_contest_id: contestId, p_storage_path: path })
  if (error) throw error
}

/** Returns the votes the caller has left. */
export async function voteFor(entryId: string): Promise<number> {
  const { data, error } = await supabase.rpc('vote_photo_entry', { p_entry_id: entryId })
  if (error) throw error
  return Number(data ?? 0)
}

/** Returns the votes the caller has left. */
export async function takeVoteBack(entryId: string): Promise<number> {
  const { data, error } = await supabase.rpc('withdraw_photo_vote', { p_entry_id: entryId })
  if (error) throw error
  return Number(data ?? 0)
}

export type DisqualifyReason = 'off_objective' | 'not_our_work' | 'safety' | 'quality' | 'other'
export const DISQUALIFY_REASONS: readonly DisqualifyReason[] = ['off_objective', 'not_our_work', 'safety', 'quality', 'other']

export async function disqualify(entryId: string, reason: DisqualifyReason, note: string | null): Promise<void> {
  const { error } = await supabase.rpc('disqualify_photo_entry', { p_entry_id: entryId, p_reason: reason, p_note: note })
  if (error) throw error
}

export async function reinstate(entryId: string): Promise<void> {
  const { error } = await supabase.rpc('reinstate_photo_entry', { p_entry_id: entryId })
  if (error) throw error
}

export type ConfirmOutcome = {
  place: number
  path: string
  personId: string
  outcome: 'awarded' | 'rule_off' | 'capped' | 'not_member' | 'duplicate' | 'nothing' | 'failed'
  stars: number
  told: boolean
}

/** FINAL: the winners are recorded and paid, bells ring, per-person votes are deleted. */
export async function confirmResults(contestId: string, winners: { place: number; path: string }[]): Promise<ConfirmOutcome[]> {
  const { data, error } = await supabase.rpc('award_photo_contest', {
    p_contest_id: contestId,
    p_winners: winners.map((w) => ({ place: w.place, storage_path: w.path })),
  })
  if (error) throw error
  return ((data ?? []) as Row[]).map((r) => ({
    place: Number(r.place),
    path: r.storage_path as string,
    personId: r.person_id as string,
    outcome: r.outcome as ConfirmOutcome['outcome'],
    stars: Number(r.stars) || 0,
    told: r.told === true,
  }))
}

/**
 * The vote places a judge is offered: every qualified photo with a vote, most
 * votes first, as many as there are vote places. The server checks the same
 * rule (award_photo_contest); this only proposes it. `tiedWith` names the
 * photos a placed photo is level with, which is the only choice a judge
 * actually makes on a vote — and the order among them is theirs.
 */
export function proposeVotePlaces(
  entries: { path: string; votes: number | null }[],
  votePlaces: number,
): { placed: string[]; ties: string[][] } {
  const counted = entries.filter((e) => (e.votes ?? 0) > 0).sort((a, b) => (b.votes ?? 0) - (a.votes ?? 0))
  const placed = counted.slice(0, votePlaces).map((e) => e.path)
  const cutoff = counted[Math.min(votePlaces, counted.length) - 1]?.votes ?? null
  const groups = new Map<number, string[]>()
  for (const e of counted) {
    const v = e.votes ?? 0
    if (cutoff !== null && v >= cutoff) groups.set(v, [...(groups.get(v) ?? []), e.path])
  }
  const ties = [...groups.values()].filter((g) => g.length > 1)
  return { placed, ties }
}

/** Swap two placed photos — allowed only when they are level on votes. */
export function swapIfTied(placed: string[], i: number, j: number, votesOf: (path: string) => number): string[] {
  if (i < 0 || j < 0 || i >= placed.length || j >= placed.length) return placed
  if (votesOf(placed[i]) !== votesOf(placed[j])) return placed
  const next = [...placed]
  ;[next[i], next[j]] = [next[j], next[i]]
  return next
}

/* ── organising ──────────────────────────────────────────────────────────── */

export type ContestDraft = {
  id: string | null
  kind: ContestKind
  texts: ContestTexts
  startsOn: string
  endsOn: string
  voteEndsOn: string | null
  timezone: string
  photosSince: string | null
  placeStars: number[]
  judgesAwardStars: number[]
  judges: string[]
  votesPerPerson: number
  maxEntries: number | null
}

/** Create (id null) or replace a contest. Every value, every time (rule 9). */
export async function saveContest(d: ContestDraft): Promise<string> {
  const { data, error } = await supabase.rpc('save_photo_contest', {
    p_id: d.id,
    p_kind: d.kind,
    p_texts: cleanTexts(d.texts),
    p_starts_on: d.startsOn,
    p_ends_on: d.endsOn,
    p_vote_ends_on: d.kind === 'vote' ? d.voteEndsOn : null,
    p_timezone: d.timezone,
    p_photos_since: d.photosSince,
    p_place_stars: d.placeStars,
    p_judges_award_stars: d.kind === 'vote' ? d.judgesAwardStars : [],
    p_judges: d.judges,
    p_votes_per_person: d.votesPerPerson,
    p_max_entries: d.maxEntries,
  })
  if (error) throw error
  return data as string
}

/** A locale with no title is not sent; empty objective/prize go as null. */
export function cleanTexts(texts: ContestTexts): ContestTexts {
  const out: ContestTexts = {}
  for (const [k, v] of Object.entries(texts) as [ContestLocale, ContestWords | undefined][]) {
    if (!v || !v.title.trim()) continue
    out[k] = { title: v.title.trim(), objective: v.objective?.trim() || null, prize: v.prize?.trim() || null }
  }
  return out
}

export async function startMonthly(d: {
  texts: ContestTexts
  timezone: string
  /** 151. null = the whole of the next month (the default). */
  voteDays: number | null
  placeStars: number[]
  judgesAwardStars: number[]
  judges: string[]
  votesPerPerson: number
}): Promise<string> {
  const { data, error } = await supabase.rpc('start_monthly_photo_contest', {
    p_texts: cleanTexts(d.texts),
    p_timezone: d.timezone,
    p_vote_days: d.voteDays,
    p_place_stars: d.placeStars,
    p_judges_award_stars: d.judgesAwardStars,
    p_judges: d.judges,
    p_votes_per_person: d.votesPerPerson,
  })
  if (error) throw error
  return data as string
}

/** 151. null = the whole of the next month. */
export async function setVoteDays(seriesId: string, days: number | null): Promise<void> {
  const { error } = await supabase.rpc('set_photo_contest_vote_days', { p_series_id: seriesId, p_vote_days: days })
  if (error) throw error
}

export async function endMonthly(seriesId: string): Promise<void> {
  const { error } = await supabase.rpc('end_monthly_photo_contest', { p_series_id: seriesId })
  if (error) throw error
}

export async function cancelContest(id: string): Promise<void> {
  const { error } = await supabase.rpc('cancel_photo_contest', { p_id: id })
  if (error) throw error
}

/** "2026-10-01" → "October 2026" in the reader's language (a month of the monthly contest). */
export function monthLabel(periodMonth: string, lang: HubLang): string {
  const [y, m] = periodMonth.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, 15)).toLocaleDateString(lang === 'es' ? 'es-CL' : 'en-CA', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

/** A YYYY-MM-DD date as the reader reads dates — never shifted by their zone. */
export function dayLabel(day: string, lang: HubLang): string {
  const [y, m, d] = day.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString(lang === 'es' ? 'es-CL' : 'en-CA', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
}
